import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `finance` domain.
 *
 * Every claim here was read out of the code, not the spec. The three that most
 * often surprise an operator, and which the copy therefore leads with:
 *
 *   - a withdrawal debits the wallet when the PLAYER submits it
 *     (`createWithdraw` posts `-amount` immediately); Approve and Mark Paid
 *     move no money at all
 *   - "Prev/New balance" on both money tables is the CASH POOL either side of
 *     that one posting, never the member's total balance
 *   - the settings dialog edits ONE LAYER at a time — the platform default every
 *     brand falls back to, or one brand's own overrides — and saving on a brand
 *     pins every value on screen to it, so a later platform change stops
 *     reaching that brand with nothing on screen to say so
 */
export const financeGuide: DomainGuide = {
  domain: 'finance',
  pages: [
    {
      id: 'finance.deposits',
      route: '/finance?tab=deposits',
      permission: 'deposit:read',
      status: 'live',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'memberAccount', kind: 'text' },
        { key: 'depositSource', kind: 'text' },
        { key: 'channelAccount', kind: 'text' },
        { key: 'prevBalance', kind: 'money-unsigned' },
        { key: 'amount', kind: 'money-unsigned' },
        { key: 'newBalance', kind: 'money-unsigned', formula: 'newBalance = prevBalance + amount' },
        { key: 'activity', kind: 'status' },
      ],
      cautionCount: 10,
      warningCount: 1,
    },
    {
      id: 'finance.withdraws',
      route: '/finance?tab=withdraws',
      permission: 'withdraw:read',
      status: 'live',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'memberAccount', kind: 'text' },
        { key: 'withdrawSource', kind: 'text' },
        { key: 'channelAccount', kind: 'text' },
        { key: 'prevBalance', kind: 'money-unsigned' },
        { key: 'amount', kind: 'money-unsigned' },
        { key: 'newBalance', kind: 'money-unsigned', formula: 'newBalance = prevBalance − amount' },
        { key: 'activity', kind: 'status' },
      ],
      cautionCount: 11,
      warningCount: 3,
    },
    {
      id: 'finance.banks',
      route: '/finance?tab=banks',
      permission: 'bank_account:read',
      status: 'live',
      fields: [
        { key: 'bankType', kind: 'text' },
        { key: 'bank', kind: 'text' },
        { key: 'accountNumber', kind: 'text' },
        { key: 'accountName', kind: 'text' },
        { key: 'isActive', kind: 'status' },
        { key: 'autoRotate', kind: 'status' },
      ],
      cautionCount: 5,
      warningCount: 1,
    },
    {
      // `verified` is operator-settable, is frozen onto the withdrawal row at
      // request time — and refuses nothing: the gate that would read it
      // (FINANCE_REQUIRE_VERIFIED_BANK) defaults false and is set in no manifest.
      id: 'finance.memberBanks',
      route: '/finance?tab=member-banks',
      permission: 'member:read',
      status: 'partially-wired',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'member', kind: 'text' },
        { key: 'bank', kind: 'text' },
        { key: 'account', kind: 'text' },
        { key: 'verified', kind: 'status' },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      // Status is deliberate, and only half true: the DEPOSIT ceiling is real at
      // the money edge, while withdrawal approval and the two wallet limits are
      // stored and read back only. The amber banner is what stops a manager
      // setting 1,000 on the wallet row and believing a 5,000 adjustment will
      // now be refused.
      id: 'finance.approvalLimits',
      route: '/finance?tab=approval-limits',
      permission: 'approval_limit:read',
      status: 'records-intent',
      fields: [
        { key: 'subject', kind: 'text' },
        { key: 'deposit', kind: 'money-unsigned' },
        { key: 'withdraw', kind: 'money-unsigned' },
        { key: 'adjust_add', kind: 'money-unsigned' },
        { key: 'adjust_sub', kind: 'money-unsigned' },
      ],
      cautionCount: 5,
      warningCount: 2,
    },
    {
      /**
       * `permission` names what the GATEWAY enforces. That is the
       * `:create` code on its own exact-path policy — `POST /deposits` and
       * `POST /withdraws` no longer ride the `:approve` write policies, so
       * filing a record and deciding one are two grants. The withdraw twin is
       * `withdraw:create`; this names the deposit one because the tab opens on
       * that half.
       */
      id: 'finance.makeup',
      route: '/finance?tab=makeup',
      permission: 'deposit:create',
      status: 'live',
      fields: [
        { key: 'member', kind: 'text' },
        { key: 'kind', kind: 'text' },
        { key: 'amount', kind: 'money-unsigned' },
        { key: 'channel', kind: 'text' },
        { key: 'houseAccount', kind: 'text' },
        { key: 'memberAccount', kind: 'text' },
        { key: 'remark', kind: 'text' },
      ],
      cautionCount: 6,
      warningCount: 3,
    },
    {
      id: 'finance.config',
      route: '/finance (settings dialog)',
      permission: 'setting:update',
      status: 'live',
      fields: [
        { key: 'depositEnabled', kind: 'status' },
        { key: 'withdrawEnabled', kind: 'status' },
        // Platform-wide, unlike everything else in this dialog. It is listed so
        // the help says which control an operator cannot set per brand.
        { key: 'bettingEnabled', kind: 'status' },
        { key: 'depositQuickAmounts', kind: 'money-unsigned' },
        { key: 'withdrawQuickAmounts', kind: 'money-unsigned' },
      ],
      cautionCount: 6,
      warningCount: 2,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
